import { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { groupServices } from "@/utils/groupServices";

const PRI_TONE = { high: "text-rose-600 bg-rose-50", medium: "text-amber-600 bg-amber-50", low: "text-slate-500 bg-slate-100" };
const TT_LABEL = { tax_profile: "Tax Profile", organizer: "Organizer", service: "Service", addon: "Add-on", always: "Always" };
const OP_LABEL = { is_yes: "is Yes", is_no: "is No", equals: "equals", not_equals: "≠", in: "in", exists: "exists" };
const inp = "w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-200";
const lbl = "text-xs font-medium text-slate-500 mb-1 block";

const BLANK = {
  rule_name: "", trigger_type: "tax_profile", trigger_operator: "is_yes", trigger_value: "Yes",
  tax_profile_field: "", organizer_field: "", required_document: "", applies_to_service: "any",
  applies_to_addon: "any", requirement: "required", client_explanation: "", internal_notes: "",
  priority: "medium", active: true,
};

function RuleDialog({ open, onClose, meta, editing, onSaved }) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(editing ? { ...BLANK, ...editing } : BLANK); }, [editing, open]);
  const set = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    if (k === "trigger_type") {
      if (v !== "tax_profile") next.tax_profile_field = "";
      if (v !== "organizer") next.organizer_field = "";
    }
    return next;
  });
  if (!meta) return null;

  const save = async () => {
    if (!form.rule_name.trim() || !form.required_document) { toast.error("Rule name and required document are required"); return; }
    setSaving(true);
    try {
      if (editing?.id) await api.patch(`/document-rules/${editing.id}`, form);
      else await api.post("/document-rules", form);
      toast.success(editing?.id ? "Rule updated" : "Rule created"); onSaved(); onClose();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dr-dialog">
        <DialogHeader>
          <DialogTitle>{editing?.id ? "Edit Rule" : "New Document Rule"}</DialogTitle>
          <DialogDescription>Configure when a document is requested. The Business Rules Engine evaluates active rules against the client's organizer answers (and, where targeted, service/add-ons) to create document requests deterministically.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className={lbl}>Rule Name *</label>
            <input className={inp} value={form.rule_name} onChange={(e) => set("rule_name", e.target.value)} data-testid="dr-name" placeholder="e.g. W-2 — Employment Income" />
          </div>
          <div>
            <label className={lbl}>Trigger Type</label>
            <select className={inp} value={form.trigger_type} onChange={(e) => set("trigger_type", e.target.value)} data-testid="dr-trigger-type">
              {meta.trigger_types.map((t) => <option key={t} value={t}>{TT_LABEL[t]}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Operator</label>
            <select className={inp} value={form.trigger_operator} onChange={(e) => set("trigger_operator", e.target.value)} data-testid="dr-operator">
              {meta.operators.map((o) => <option key={o} value={o}>{OP_LABEL[o]}</option>)}
            </select>
          </div>
          {form.trigger_type === "tax_profile" && (
            <div className="md:col-span-2">
              <label className={lbl}>Tax Profile Field</label>
              <select className={inp} value={form.tax_profile_field || ""} onChange={(e) => set("tax_profile_field", e.target.value)} data-testid="dr-profile-field">
                <option value="">Select field…</option>
                {meta.tax_profile_fields.map((f) => <option key={f.key} value={f.key}>{f.section} · {f.label}</option>)}
              </select>
            </div>
          )}
          {form.trigger_type === "organizer" && (
            <div className="md:col-span-2">
              <label className={lbl}>Organizer Field</label>
              <select className={inp} value={form.organizer_field || ""} onChange={(e) => set("organizer_field", e.target.value)} data-testid="dr-organizer-field">
                <option value="">Select organizer question…</option>
                {meta.organizer_fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
            </div>
          )}
          {["equals", "not_equals", "in", "is_yes", "is_no"].includes(form.trigger_operator) && (
            <div>
              <label className={lbl}>Trigger Value</label>
              <input className={inp} value={form.trigger_value || ""} onChange={(e) => set("trigger_value", e.target.value)} data-testid="dr-value" placeholder="Yes / value / a,b,c" />
            </div>
          )}
          <div>
            <label className={lbl}>Required Document *</label>
            <select className={inp} value={form.required_document} onChange={(e) => set("required_document", e.target.value)} data-testid="dr-doc">
              <option value="">Select document…</option>
              {Object.entries(meta.doc_types).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Applies To Service</label>
            <select className={inp} value={form.applies_to_service || "any"} onChange={(e) => set("applies_to_service", e.target.value)} data-testid="dr-service">
              <option value="any">Any Service</option>
              {groupServices(meta.services || [], meta.categories || []).map(({ label, items }) => (
                <optgroup key={label} label={label}>
                  {items.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </optgroup>
              ))}
            </select>
          </div>
          <div>
            <label className={lbl}>Applies To Add-on</label>
            <select className={inp} value={form.applies_to_addon || "any"} onChange={(e) => set("applies_to_addon", e.target.value)} data-testid="dr-addon">
              <option value="any">Any Add-on</option>
              {meta.addons.map((a) => <option key={a.code} value={a.code}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Requirement</label>
            <select className={inp} value={form.requirement} onChange={(e) => set("requirement", e.target.value)} data-testid="dr-requirement">
              {meta.requirements.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Priority</label>
            <select className={inp} value={form.priority} onChange={(e) => set("priority", e.target.value)} data-testid="dr-priority">
              {meta.priorities.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid="dr-active" className="rounded border-slate-300 text-blue-600" />Active
            </label>
          </div>
          <div className="md:col-span-2">
            <label className={lbl}>Client Explanation</label>
            <textarea className={inp} rows={2} value={form.client_explanation || ""} onChange={(e) => set("client_explanation", e.target.value)} data-testid="dr-explanation" placeholder="Client-friendly reason this document is requested" />
          </div>
          <div className="md:col-span-2">
            <label className={lbl}>Internal Notes</label>
            <textarea className={inp} rows={2} value={form.internal_notes || ""} onChange={(e) => set("internal_notes", e.target.value)} data-testid="dr-notes" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="h-9 px-4 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="dr-save" className="h-9 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-1.5">
            {saving && <Icons.Loader2 className="h-4 w-4 animate-spin" />}Save Rule
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function DocumentRules() {
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState({ items: [], counts: {} });
  const [filters, setFilters] = useState({ q: "", trigger_type: "all", active: "all" });
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { q: filters.q || undefined, trigger_type: filters.trigger_type,
                       active: filters.active === "all" ? undefined : filters.active === "active" };
      const { data } = await api.get("/document-rules", { params });
      setData(data);
    } catch { toast.error("Could not load rules"); } finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { api.get("/document-rules/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this rule?")) return;
    try { await api.delete(`/document-rules/${id}`); toast.success("Rule deleted"); load(); } catch { toast.error("Delete failed"); }
  };
  const seed = async () => {
    try { const { data } = await api.post("/document-rules/seed"); toast.success(`Seeded ${data.seeded} rule(s)`); load(); }
    catch { toast.error("Seed failed"); }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-5" data-testid="document-rules-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#171B2E]">Document Requirement Rules</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">Configurable rules that drive personalized document requests. The Business Rules Engine evaluates these deterministically against each client's organizer answers (with optional service/add-on targeting) whenever their facts change — one authoritative source, nothing hardcoded.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={seed} data-testid="dr-seed" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"><Icons.Sparkles className="h-4 w-4" />Seed from Organizer Map</button>
          <button onClick={() => setDialog({ open: true, editing: null })} data-testid="dr-new" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold"><Icons.Plus className="h-4 w-4" />New Rule</button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input className={cn(inp, "pl-9")} placeholder="Search rules…" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} data-testid="dr-search" />
        </div>
        <select className={cn(inp, "w-44")} value={filters.trigger_type} onChange={(e) => setFilters((f) => ({ ...f, trigger_type: e.target.value }))} data-testid="dr-filter-type">
          <option value="all">All trigger types</option>
          {meta?.trigger_types.map((t) => <option key={t} value={t}>{TT_LABEL[t]}</option>)}
        </select>
        <select className={cn(inp, "w-36")} value={filters.active} onChange={(e) => setFilters((f) => ({ ...f, active: e.target.value }))} data-testid="dr-filter-active">
          <option value="all">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option>
        </select>
        <span className="text-xs text-slate-400 ml-auto">{data.counts.active || 0} active / {data.counts.total || 0} total</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Icons.Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
      ) : data.items.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Icons.FileSearch className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p>No rules yet. Seed from the Organizer Map or create one.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {data.items.map((r) => (
            <div key={r.id} className={cn("bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-start gap-4", !r.active && "opacity-60")} data-testid={`dr-row-${r.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-[#171B2E] text-sm">{r.rule_name}</span>
                  <span className={cn("text-[11px] font-medium px-2 py-0.5 rounded-full", PRI_TONE[r.priority])}>{r.priority}</span>
                  {r.requirement === "required" ? <span className="text-[11px] text-rose-600">Required</span> : <span className="text-[11px] text-slate-400">Optional</span>}
                  {!r.active && <span className="text-[11px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">Inactive</span>}
                </div>
                <div className="text-xs text-slate-500 mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="inline-flex items-center gap-1"><Icons.Zap className="h-3 w-3 text-amber-500" />
                    {TT_LABEL[r.trigger_type]}{r.tax_profile_field ? ` · ${r.tax_profile_field}` : r.organizer_field ? ` · ${r.organizer_field}` : ""} {OP_LABEL[r.trigger_operator]} {r.trigger_value || ""}
                  </span>
                  <Icons.ArrowRight className="h-3 w-3 text-slate-300" />
                  <span className="inline-flex items-center gap-1 text-indigo-600"><Icons.FileText className="h-3 w-3" />{r.required_document_label}</span>
                </div>
                {r.client_explanation && <div className="text-[11px] text-slate-400 mt-1 italic">"{r.client_explanation}"</div>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => setDialog({ open: true, editing: r })} data-testid={`dr-edit-${r.id}`} className="h-8 w-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"><Icons.Pencil className="h-4 w-4" /></button>
                <button onClick={() => del(r.id)} data-testid={`dr-delete-${r.id}`} className="h-8 w-8 rounded-lg hover:bg-rose-50 flex items-center justify-center text-slate-400 hover:text-rose-600"><Icons.Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <RuleDialog open={dialog.open} onClose={() => setDialog({ open: false, editing: null })} meta={meta} editing={dialog.editing} onSaved={load} />
    </div>
  );
}
